import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAppDispatch } from '../../../redux/HooksRedux';
import { RootState } from '../../../redux/rootReducer';
import { FederalStateFormI } from '../../../../types/interface';
import { FederalStateValidationSet } from 'utils/validations/ValidationsSet/FederalStateValidationSet';
import validationFunctions from 'utils/validationUtils';
import {
  capitalizeFirstLetter,
  checkAccess,
  parseIntoNumber
} from 'utils/helpers';
import { modules } from 'utils/enums/misc';
import * as federalStateAction from '../../../redux/FederalState/FederalStateAction';
import {
  errorNotification,
  successNotification
} from 'base/notifications/Notifications';
import { FEDERAL_STATE } from 'constant/RoutesUrls';
import { AxiosError } from 'axios';
import DeleteModel from 'base/DeleteModal/DeleteModal';
import { Card, Dropdown, Form } from 'react-bootstrap';
import ConditionalRender from 'base/ConditionalRender/ConditionalRender';
import { StandardLoader } from 'base/Loader/Loader';
import { t } from 'i18next';
import { StandardButton } from 'base/StandardButton/StandardButton';
import StandardInput from 'base/StandardInput/StandardInput';

const FederalStateForm = () => {
  const navigate = useNavigate();
  const [URLSearchParams] = useSearchParams();
  const editFederalStateId = URLSearchParams.get('editFederalStateId');
  const dispatch = useAppDispatch();
  const userPermission = useSelector(
    (state: RootState) => state.userProfile.permission
  );

  const [showError, setShowError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [deleteFederalState, setDeleteFederalState] = useState<{
    state_id: number;
    showPopup: boolean;
  }>({
    state_id: 0,
    showPopup: false
  });

  const [formData, setFormData] = useState<FederalStateFormI>({
    state_name_en: '',
    state_name_de: ''
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    const validate: boolean = validationFunctions.checkFormValidity(
      { ...formData },
      { ...FederalStateValidationSet }
    );

    if (!validate) {
      setShowError(true);
      setIsLoading(false);
      return;
    }

    if (
      editFederalStateId &&
      checkAccess([modules.FEDERAL_STATE.edit], userPermission)
    ) {
      try {
        const response = await dispatch(
          federalStateAction.updateFederalState(
            parseIntoNumber(editFederalStateId),
            { ...formData }
          )
        );

        if (response.status === 200) {
          successNotification(t('federal_state.notification.update_success'));
          navigate(FEDERAL_STATE);
          setIsLoading(false);
        } else {
          errorNotification(response.data?.message);
          setIsLoading(false);
        }
      } catch (error) {
        if (error instanceof Error) {
          errorNotification(error.message);
        } else if (
          error instanceof AxiosError &&
          error?.response?.data?.message
        ) {
          errorNotification(
            error?.response?.data?.message || error.response?.data?.error
          );
        }
        console.log(error);
        setIsLoading(false);
      }
    } else if (checkAccess([modules.FEDERAL_STATE.create], userPermission)) {
      try {
        const response = await dispatch(
          federalStateAction.createFederalState(formData)
        );

        if (response?.status === 201) {
          successNotification(t('federal_state.notification.create_success'));
          navigate(FEDERAL_STATE);
          setIsLoading(false);
        } else {
          errorNotification(response?.data?.message);
          setIsLoading(false);
        }
      } catch (error) {
        console.log(error);
        setIsLoading(false);
      }
    } else {
      navigate(FEDERAL_STATE);
      setIsLoading(false);
    }
  };

  const getSingleFederalState = async () => {
    if (!checkAccess([modules.FEDERAL_STATE.edit], userPermission)) {
      navigate(FEDERAL_STATE);
      return;
    }
    setIsLoading(true);
    try {
      const response = await dispatch(
        federalStateAction.getSingleFederalState(
          parseIntoNumber(editFederalStateId)
        )
      );

      if (response?.status === 200) {
        setFormData({
          state_name_en: response.data.data.state_name_en,
          state_name_de: response.data.data.state_name_de
        });
        setIsLoading(false);
      } else {
        errorNotification(response?.data?.message);
        navigate(FEDERAL_STATE);
        setIsLoading(false);
      }
    } catch (error) {
      console.log(error);
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (editFederalStateId) {
      getSingleFederalState();
    }
  }, []);

  const handleDeleteFederalState = async () => {
    setIsLoading(true);
    try {
      const response = await dispatch(
        federalStateAction.deleteFederalState(deleteFederalState.state_id)
      );
      if (response?.status === 200) {
        successNotification(response.data?.message);
        setDeleteFederalState({ state_id: 0, showPopup: false });
        navigate(FEDERAL_STATE);
        setIsLoading(false);
      } else {
        errorNotification(response?.data?.message);
        setDeleteFederalState({ state_id: 0, showPopup: false });
        setIsLoading(false);
      }
    } catch (error) {
      setDeleteFederalState({ state_id: 0, showPopup: false });
      console.log(error);
      setIsLoading(false);
    }
  };

  const renderDeletePopup = () => {
    if (deleteFederalState.showPopup) {
      return (
        <DeleteModel
          onCancel={() =>
            setDeleteFederalState({ state_id: 0, showPopup: false })
          }
          onConfirm={handleDeleteFederalState}
        />
      );
    }
  };

  return (
    <Card>
      <ConditionalRender condition={!isLoading} fallBack={<StandardLoader />}>
        <Form onSubmit={handleSubmit}>
          <Card.Header className="py-2">
            <div className="row justify-content-between align-items-center gap-2">
              <div className="w-auto">
                <h3>{capitalizeFirstLetter(t('federal_state.form.header'))}</h3>
              </div>
              <div className="d-flex gap-2 w-auto">
                <StandardButton
                  onClick={() => {
                    navigate(FEDERAL_STATE);
                  }}
                  buttonText={t('general.common_text.text_15')}
                  variant="outline-danger"
                />
                <StandardButton
                  onClick={handleSubmit}
                  type="submit"
                  buttonText={
                    editFederalStateId
                      ? t('general.common_text.text_18')
                      : t('general.common_text.text_2')
                  }
                />
                <ConditionalRender condition={!!editFederalStateId}>
                  <Dropdown
                    id="scroll_in_table"
                    placement="auto"
                    className="position-static"
                  >
                    <Dropdown.Toggle className="no-padding no-margin p-0 d-flex bg-transparent">
                      <StandardButton
                        buttonText={capitalizeFirstLetter(
                          t('general.common_text.text_8')
                        )}
                        onClick={() => {}}
                        variant="outline-danger"
                      />
                    </Dropdown.Toggle>
                    <Dropdown.Menu className="p-0">
                      <ConditionalRender
                        condition={checkAccess(
                          [modules.FEDERAL_STATE.delete],
                          userPermission
                        )}
                      >
                        <Dropdown.Item
                          onClick={() => {
                            setDeleteFederalState({
                              state_id: parseIntoNumber(editFederalStateId),
                              showPopup: true
                            });
                          }}
                        >
                          <span className="d-flex fs-16 font-normal p-0">
                            <p className="mb-0">
                              {capitalizeFirstLetter(
                                t('general.common_text.text_14')
                              )}
                            </p>
                          </span>
                        </Dropdown.Item>
                      </ConditionalRender>
                    </Dropdown.Menu>
                  </Dropdown>
                </ConditionalRender>
              </div>
            </div>
          </Card.Header>
          <Card.Body>
            <div className="row">
              <div className="col-12 col-md-6">
                <StandardInput
                  label={t('federal_state.form.text_1')}
                  placeholder={t('federal_state.form.text_1')}
                  value={formData.state_name_en}
                  onChange={e =>
                    setFormData({ ...formData, state_name_en: e as string })
                  }
                  className=""
                  showError={showError}
                  type="text"
                  validations={FederalStateValidationSet.state_name_en}
                />
              </div>
              <div className="col-12 col-md-6">
                <StandardInput
                  label={t('federal_state.form.text_2')}
                  placeholder={t('federal_state.form.text_2')}
                  value={formData.state_name_de}
                  onChange={e =>
                    setFormData({ ...formData, state_name_de: e as string })
                  }
                  className=""
                  showError={showError}
                  type="text"
                  validations={FederalStateValidationSet.state_name_de}
                />
              </div>
            </div>
          </Card.Body>
        </Form>
      </ConditionalRender>
      {renderDeletePopup()}
    </Card>
  );
};

export default FederalStateForm;
